import { isExcludedTerm } from './termSides';

/**
 * Related terms the user explicitly turned ON that were OFF by default (beyond the per-term
 * top-N), split by the side the parent term sits on. The mirror of buildDeselectionPayload:
 * it captures positive picks from "the remainder" so they can feed the ranking signal.
 *
 * A chip is emitted only when it is:
 *   - in `explicitlySelectedSynonyms` (the user toggled it on individually — bulk "select all"
 *     never populates this, so mass-selection isn't logged), AND
 *   - still selected in `effectiveSynonyms` (survived to submit), AND
 *   - NOT in `initiallySelectedSynonyms` for that term (it was default-off — a genuine deviation).
 *
 * @returns {{ include: Record<string, string[]>, exclude: Record<string, string[]> }}
 */
export function buildSelectionPayload({
  explicitlySelectedSynonyms,
  effectiveSynonyms,
  termEnabled,
  synonymsEnabled,
  termSideMap,
  initiallySelectedSynonyms,
}) {
  if (!synonymsEnabled) return { include: {}, exclude: {} };
  const explicit = explicitlySelectedSynonyms || {};
  const enabled = termEnabled || {};
  const effective = effectiveSynonyms || {};
  const initial = initiallySelectedSynonyms || {};

  const include = {};
  const exclude = {};
  for (const [term, syns] of Object.entries(explicit)) {
    if (enabled[term] === false) continue;
    const stillSelected = new Set(
      (effective[term] || []).map(s => s.toLowerCase())
    );
    const initialForTerm = new Set(
      (initial[term] || []).map(s => s.toLowerCase())
    );
    const emitted = syns.filter(s => {
      const low = s.toLowerCase();
      // Kept in the query AND not part of the default-on top-N -> a real positive pick.
      return stillSelected.has(low) && !initialForTerm.has(low);
    });
    if (emitted.length > 0) {
      const target = isExcludedTerm(termSideMap, term) ? exclude : include;
      target[term] = emitted;
    }
  }
  return { include, exclude };
}
